"use client"

import { useState, useEffect } from "react"
import axios from "axios"
import "./EquipmentPage.css"
import Header from "../components/Header"
import EquipmentSelection from "../components/EquipmentSelection"
import { Dumbbell, Plus, Trash2, Edit3, Save, X, Search, Grid, List, AlertCircle } from "lucide-react"

const EquipmentPage = () => {
  const [equipmentList, setEquipmentList] = useState([])
  const [selectedEquipment, setSelectedEquipment] = useState(null)
  const [quantity, setQuantity] = useState("")
  const [editing, setEditing] = useState(null)
  const [editName, setEditName] = useState("")
  const [editQuantity, setEditQuantity] = useState("")
  const [searchTerm, setSearchTerm] = useState("")
  const [viewMode, setViewMode] = useState("grid") // 'grid' or 'list'
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    fetchEquipment()
  }, [])

  const fetchEquipment = async () => {
    setLoading(true)
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/equipment`, {
        headers: { auth: document.cookie.replace(/(?:(?:^|.*;\s*)auth\s*=\s*([^;]*).*$)|^.*$/, "$1") },
      })
      setEquipmentList(response.data)
    } catch (error) {
      console.error("Error fetching equipment:", error)
    } finally {
      setLoading(false)
    }
  }

  const handleAddEquipment = async () => {
    if (!selectedEquipment) return
    setLoading(true)
    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/equipment`,
        {
          name: selectedEquipment.equipment_name,
          quantity: 1,
        },
        {
          headers: { auth: document.cookie.replace(/(?:(?:^|.*;\s*)auth\s*=\s*([^;]*).*$)|^.*$/, "$1") },
        },
      )
      setSelectedEquipment(null)
      setQuantity("")
      fetchEquipment()
    } catch (error) {
      console.error("Error adding equipment:", error)
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteEquipment = async (id) => {
    setLoading(true)
    try {
      await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/equipment/${id}`, {
        headers: { auth: document.cookie.replace(/(?:(?:^|.*;\s*)auth\s*=\s*([^;]*).*$)|^.*$/, "$1") },
      })
      fetchEquipment()
    } catch (error) {
      console.error("Error deleting equipment:", error)
    } finally {
      setLoading(false)
    }
  }

  const handleEditEquipment = async () => {
    setLoading(true)
    try {
      await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/equipment/${editing}`,
        {
          name: editName,
          quantity: Number.parseInt(editQuantity),
        },
        {
          headers: { auth: document.cookie.replace(/(?:(?:^|.*;\s*)auth\s*=\s*([^;]*).*$)|^.*$/, "$1") },
        },
      )
      setEditing(null)
      setEditName("")
      setEditQuantity("")
      fetchEquipment()
    } catch (error) {
      console.error("Error updating equipment:", error)
    } finally {
      setLoading(false)
    }
  }

  const filteredEquipment = equipmentList.filter((equip) => equip.name.toLowerCase().includes(searchTerm.toLowerCase()))

  return (
    <div className="equipment-page">
      <Header />

      <div className="equipment-container">
        {/* Hero Section */}
        <div className="equipment-hero">
          <div className="hero-content">
            <div className="hero-text">
              <h1>
                <Dumbbell className="hero-icon" />
                Equipment Management
              </h1>
              <p>Manage your gym equipment inventory with ease</p>
            </div>
            <div className="hero-stats">
              <div className="stat-card">
                <span className="stat-number">{equipmentList.length}</span>
                <span className="stat-label">Total Equipment</span>
              </div>
            </div>
          </div>
        </div>

        <div className="equipment-content">
          {/* Controls Section */}
          <div className="controls-section">
            <div className="search-filter">
              <div className="search-box">
                <Search className="search-icon" />
                <input
                  type="text"
                  placeholder="Search equipment..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
              </div>
            </div>

            <div className="view-controls">
              <button className={`view-btn ${viewMode === "grid" ? "active" : ""}`} onClick={() => setViewMode("grid")}>
                <Grid className="view-icon" />
                Grid
              </button>
              <button className={`view-btn ${viewMode === "list" ? "active" : ""}`} onClick={() => setViewMode("list")}>
                <List className="view-icon" />
                List
              </button>
            </div>
          </div>

          {/* Equipment Display */}
          {loading ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading equipment...</p>
            </div>
          ) : filteredEquipment.length === 0 ? (
            <div className="empty-state">
              <AlertCircle className="empty-icon" />
              <h3>No Equipment Found</h3>
              <p>
                {searchTerm
                  ? `No equipment matches "${searchTerm}"`
                  : "Start building your equipment inventory by adding your first piece of equipment below."}
              </p>
            </div>
          ) : (
            <div className={`equipment-display ${viewMode}`}>
              {viewMode === "grid" ? (
                <div className="equipment-grid">
                  {filteredEquipment.map((equip) => (
                    <div key={equip.id} className="equipment-card">
                      <div className="card-header">
                        <div className="equipment-icon">
                          <Dumbbell />
                        </div>
                        <div className="card-actions">
                          <button
                            className="action-btn edit"
                            onClick={() => {
                              setEditing(equip.id)
                              setEditName(equip.name)
                              setEditQuantity(equip.quantity)
                            }}
                            title="Edit equipment"
                          >
                            <Edit3 />
                          </button>
                          <button
                            className="action-btn delete"
                            onClick={() => handleDeleteEquipment(equip.id)}
                            title="Delete equipment"
                          >
                            <Trash2 />
                          </button>
                        </div>
                      </div>

                      <div className="card-content">
                        {editing === equip.id ? (
                          <div className="edit-form">
                            <input
                              type="text"
                              value={editName}
                              onChange={(e) => setEditName(e.target.value)}
                              className="edit-input"
                              placeholder="Equipment name"
                            />
                            <div className="edit-actions">
                              <button className="btn btn-save" onClick={handleEditEquipment}>
                                <Save />
                                Save
                              </button>
                              <button
                                className="btn btn-cancel"
                                onClick={() => {
                                  setEditing(null)
                                  setEditName("")
                                  setEditQuantity("")
                                }}
                              >
                                <X />
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <h3 className="equipment-name">{equip.name}</h3>
                            <div className="equipment-meta">
                              <span className="equipment-id">ID: {equip.id}</span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="equipment-table-container">
                  <table className="equipment-table">
                    <thead>
                      <tr>
                        <th>Equipment</th>
                        <th>Name</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredEquipment.map((equip) => (
                        <tr key={equip.id}>
                          <td>
                            <div className="table-equipment-icon">
                              <Dumbbell />
                            </div>
                          </td>
                          <td>
                            {editing === equip.id ? (
                              <input
                                type="text"
                                value={editName}
                                onChange={(e) => setEditName(e.target.value)}
                                className="table-edit-input"
                              />
                            ) : (
                              <div className="equipment-info">
                                <span className="equipment-name">{equip.name}</span>
                                <span className="equipment-id">ID: {equip.id}</span>
                              </div>
                            )}
                          </td>
                          <td>
                            <div className="table-actions">
                              {editing === equip.id ? (
                                <>
                                  <button className="btn btn-save" onClick={handleEditEquipment}>
                                    <Save />
                                    Save
                                  </button>
                                  <button
                                    className="btn btn-cancel"
                                    onClick={() => {
                                      setEditing(null)
                                      setEditName("")
                                      setEditQuantity("")
                                    }}
                                  >
                                    <X />
                                    Cancel
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    className="btn btn-edit"
                                    onClick={() => {
                                      setEditing(equip.id)
                                      setEditName(equip.name)
                                      setEditQuantity(equip.quantity)
                                    }}
                                  >
                                    <Edit3 />
                                    Edit
                                  </button>
                                  <button className="btn btn-delete" onClick={() => handleDeleteEquipment(equip.id)}>
                                    <Trash2 />
                                    Delete
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Add Equipment Section */}
          <div className="add-equipment-section">
            <div className="section-header">
              <h2>
                <Plus className="section-icon" />
                Add New Equipment
              </h2>
              <p>Select equipment from our catalog to add to your gym</p>
            </div>

            {selectedEquipment && (
              <div className="selected-equipment">
                <div className="selected-header">
                  <h3>Selected Equipment</h3>
                  <button className="clear-selection" onClick={() => setSelectedEquipment(null)}>
                    <X />
                  </button>
                </div>
                <div className="selected-content">
                  <div className="selected-icon" dangerouslySetInnerHTML={{ __html: selectedEquipment.icon_svg }} />
                  <div className="selected-info">
                    <span className="selected-name">{selectedEquipment.equipment_name}</span>
                    <span className="selected-description">Ready to add to your inventory</span>
                  </div>
                </div>
              </div>
            )}

            <div className="add-equipment-actions">
              <button
                className="btn btn-add-primary"
                onClick={handleAddEquipment}
                disabled={!selectedEquipment || loading}
              >
                <Plus />
                {loading ? "Adding..." : "Add Equipment"}
              </button>
            </div>

            <EquipmentSelection onSelect={setSelectedEquipment} />
          </div>
        </div>
      </div>
    </div>
  )
}

export default EquipmentPage
